import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAgent,
  AgentDetail,
  getAllLanguages,
  Language,
  fetchTags,
  createTag,
  Tag,
  getProviderManifest,
  ProviderManifest,
} from "../../services/api";
import { updateExistingAgent } from "../../utils/agentOperations";
import LoadingState from "../Common/LoadingState";
import StrategySelector from "./StrategySelector";
import RealtimeControlsCard from "./StrategyConfig/RealtimeControlsCard";
import { PromptEditorModal } from "./PromptEditorModal";
import { validatePromptVariables } from "../../utils/promptValidation";
import { Strategy, StrategyProviders } from "../../types/strategies";
import {
  StrategyConfigDraft,
  DEFAULT_STT_LLM_TTS_CONFIG,
  DEFAULT_LLM_TTS_CONFIG,
  DEFAULT_VOICE2VOICE_CONFIG,
  cloneStrategyConfig,
  defaultProvidersState,
} from "../../types/strategyConfig";

// Extend the AgentDetail interface to include tag_ids
interface ExtendedAgentDetail extends Omit<AgentDetail, "tags"> {
  tag_ids?: number[];
  tags?: Tag[];
  access_token?: string;
  model_name?: string;
  proactive_audio?: boolean;
  enable_affective_dialog?: boolean;
  strategy: Strategy | null;
  strategy_providers: StrategyProviders | null;
  strategy_config?: StrategyConfigDraft;
  pstn_provider?: string;
}

const defaultAgent: Partial<ExtendedAgentDetail> = {
  uuid: undefined,
  name: "",
  description: "",
  startup_message: "Welcome to our service. How may I help you today?",
  system_prompt: "You are a helpful assistant...",
  limitations: [],
  temperature: 0,
  primary_language_id: undefined,
  supported_language_ids: [],
  is_outbound: false,
  enabled: true,
  is_public: true,
  phone_number: "",
  pstn_provider: "plivo",
  can_disconnect: false,
  tag_ids: [],
  access_token: "",
  model_name: "gemini-2.0-flash-live-001",
  strategy: null,
  strategy_providers: {
    v2v: null,
    llm: null,
    tts: null,
    stt: null,
  },
  strategy_config: null,
};

const AgentEdit: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [newTagName, setNewTagName] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [providerManifest, setProviderManifest] = useState<ProviderManifest | null>(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [agent, setAgent] =
    useState<Partial<ExtendedAgentDetail>>(defaultAgent);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [languageData, tagsData, manifestData] = await Promise.all([
          getAllLanguages(),
          fetchTags(),
          getProviderManifest(),
        ]);
        setLanguages(languageData);
        setTags(tagsData);
        setProviderManifest(manifestData);

        if (uuid) {
          const agentData = await getAgent(uuid);
          setAgent({
            ...agentData,
            strategy: agentData.strategy as Strategy | null,
            strategy_providers: (agentData as any).strategy_providers || {
              v2v: null,
              llm: null,
              tts: null,
              stt: null,
            },
            strategy_config: (agentData as any).strategy_config || null,
          });
        } else {
          // Redirect to agent list if no ID provided
          navigate("/admin/agents");
        }
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uuid, navigate]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setAgent((prev) => {
      const updates = { ...prev, [name]: value };
      return updates;
    });
  };

  const handlePrimaryLanguageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = parseInt(e.target.value, 10);
    setAgent((prev) => ({
      ...prev,
      primary_language_id: value,
    }));
  };

  const handleAddSupportedLanguage = () => {
    // Create a simple dropdown in a modal
    const modal = document.createElement("div");
    modal.style.position = "fixed";
    modal.style.top = "0";
    modal.style.left = "0";
    modal.style.width = "100%";
    modal.style.height = "100%";
    modal.style.backgroundColor = "rgba(0,0,0,0.5)";
    modal.style.display = "flex";
    modal.style.justifyContent = "center";
    modal.style.alignItems = "center";
    modal.style.zIndex = "1000";

    const content = document.createElement("div");
    content.style.backgroundColor = "white";
    content.style.padding = "20px";
    content.style.borderRadius = "5px";
    content.style.width = "300px";

    const header = document.createElement("h3");
    header.textContent = "Add Supported Language";
    header.style.marginBottom = "15px";

    // Get current supported languages
    const currentSupported = agent.supported_language_ids || [];

    // Create select element
    const select = document.createElement("select");
    select.style.width = "100%";
    select.style.padding = "8px";
    select.style.marginBottom = "15px";
    select.style.borderRadius = "4px";
    select.style.border = "1px solid #ccc";

    // Add options for languages not already in supported_language_ids
    languages.forEach((language) => {
      if (!currentSupported.includes(language.id)) {
        const option = document.createElement("option");
        option.value = language.id.toString();
        option.textContent = `${language.language} (${language.language_code})`;
        select.appendChild(option);
      }
    });

    // Buttons
    const buttonContainer = document.createElement("div");
    buttonContainer.style.display = "flex";
    buttonContainer.style.justifyContent = "flex-end";
    buttonContainer.style.gap = "10px";

    const cancelButton = document.createElement("button");
    cancelButton.textContent = "Cancel";
    cancelButton.style.padding = "8px 16px";
    cancelButton.style.backgroundColor = "#f1f1f1";
    cancelButton.style.border = "none";
    cancelButton.style.borderRadius = "4px";
    cancelButton.style.cursor = "pointer";

    const addButton = document.createElement("button");
    addButton.textContent = "Add";
    addButton.style.padding = "8px 16px";
    addButton.style.backgroundColor = "rgb(255, 204, 51)";
    addButton.style.color = "#140d0c";
    addButton.style.border = "none";
    addButton.style.borderRadius = "4px";
    addButton.style.cursor = "pointer";

    // Add event listeners
    cancelButton.onclick = () => {
      document.body.removeChild(modal);
    };

    addButton.onclick = () => {
      const selectedLanguageId = parseInt(select.value, 10);
      if (selectedLanguageId) {
        setAgent((prev) => {
          const currentSupported = prev.supported_language_ids || [];
          const updatedSupported = [...currentSupported, selectedLanguageId];

          // If primary language is not set, use the first supported language
          const primaryLanguage =
            prev.primary_language_id ||
            (updatedSupported.length === 1 ? selectedLanguageId : undefined);

          return {
            ...prev,
            supported_language_ids: updatedSupported,
            primary_language_id: primaryLanguage,
          };
        });
      }
      document.body.removeChild(modal);
    };

    // Assemble the modal
    buttonContainer.appendChild(cancelButton);
    buttonContainer.appendChild(addButton);

    content.appendChild(header);
    content.appendChild(select);
    content.appendChild(buttonContainer);

    modal.appendChild(content);
    document.body.appendChild(modal);
  };

  const handleRemoveSupportedLanguage = (languageId: number) => {
    setAgent((prev) => {
      const currentSupported = prev.supported_language_ids || [];
      return {
        ...prev,
        supported_language_ids: currentSupported.filter(
          (id) => id !== languageId
        ),
        // If primary language is removed from supported languages, set it to undefined
        primary_language_id:
          prev.primary_language_id === languageId
            ? undefined
            : prev.primary_language_id,
      };
    });
  };

  const handleAddTagId = (tagId: number) => {
    setAgent((prev) => {
      const currentTagIds = prev.tag_ids || [];
      if (currentTagIds.includes(tagId)) return prev; // Skip if already added
      return {
        ...prev,
        tag_ids: [...currentTagIds, tagId],
      };
    });
  };

  const handleRemoveTagId = (tagId: number) => {
    setAgent((prev) => ({
      ...prev,
      tag_ids: (prev.tag_ids || []).filter((id) => id !== tagId),
    }));
  };

  const handleCreateNewTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const createdTag = await createTag({
        name: newTagName.trim(),
        description: "",
      });

      // Add new tag to the tags list
      setTags([...tags, createdTag]);

      // Add new tag to the agent
      handleAddTagId(createdTag.id);

      // Clear the input
      setNewTagName("");
    } catch (err: any) {
      console.error("Error creating tag:", err);
      setError("Failed to create tag: " + err.message);
    }
  };

  const handleStrategyChange = (strategy: Strategy | null) => {
    let nextConfig: StrategyConfigDraft = null;
    if (strategy === Strategy.STT_LLM_TTS) {
      nextConfig = cloneStrategyConfig(DEFAULT_STT_LLM_TTS_CONFIG);
    } else if (strategy === Strategy.LLM_TTS) {
      nextConfig = cloneStrategyConfig(DEFAULT_LLM_TTS_CONFIG);
    } else if (strategy === Strategy.VOICE2VOICE) {
      nextConfig = cloneStrategyConfig(DEFAULT_VOICE2VOICE_CONFIG);
    }

    setAgent((prev) => ({
      ...prev,
      strategy: strategy,
      strategy_providers: defaultProvidersState(),
      strategy_config: nextConfig,
    }));
  };

  const handleStrategyProvidersChange = (providers: StrategyProviders) => {
    setAgent((prev) => ({
      ...prev,
      strategy_providers: providers,
    }));
  };

  const handleStrategyConfigChange = (config: StrategyConfigDraft) => {
    setAgent((prev) => ({
      ...prev,
      strategy_config: config,
    }));
  };

  const handlePstnProviderChange = (provider: string) => {
    setAgent((prev) => ({
      ...prev,
      pstn_provider: provider,
    }));
  };


  const safeStrategyProviders: StrategyProviders = useMemo(() => {
    return agent.strategy_providers || defaultProvidersState();
  }, [agent.strategy_providers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setValidationError(null);

    // Validate prompt variables
    const validation = validatePromptVariables(agent.system_prompt || "");
    if (!validation.isValid) {
      setValidationError(validation.errorMessage || "Invalid template variables found");
      setIsSubmitting(false);
      return;
    }

    try {
      setLoading(true);
      if (uuid) {
        await updateExistingAgent(uuid, agent);
        setError(null);
        navigate("/admin/agents");
      }
    } catch (err: any) {
      console.error("Failed to update agent:", err);
      setError(err.message || "Failed to update agent");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  // Find language name by ID
  const getLanguageName = (languageId: number) => {
    const language = languages.find((lang) => lang.id === languageId);
    return language
      ? `${language.language} (${language.language_code})`
      : `Unknown (${languageId})`;
  };

  // Find if a tag is selected
  const isTagSelected = (tagId: number) => {
    return (agent.tag_ids || []).includes(tagId);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <LoadingState message="Loading agent data..." fullPage={true} />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[#140d0c]">Edit Agent</h2>
          <button
            onClick={() => navigate("/admin/agents")}
            className="px-4 py-2 bg-gray-200 text-[#140d0c] rounded hover:bg-gray-300 transition-colors flex items-center gap-1"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to List
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm mb-4">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-[#f2efe3] p-6 rounded-lg border border-[#e7e2d3]">
            <h3 className="text-lg font-medium mb-4">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={agent.name || ""}
                  onChange={handleInputChange}
                  placeholder="Agent Name"
                  className="w-full px-3 py-2 border border-[#e7e2d3] rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={agent.description || ""}
                  onChange={handleInputChange}
                  placeholder="Agent Description"
                  rows={2}
                  className="w-full px-3 py-2 border border-[#e7e2d3] rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Access Token
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    name="access_token"
                    value={agent.access_token || ""}
                    onChange={handleInputChange}
                    placeholder="Access Token"
                    className="flex-1 px-3 py-2 border border-[#e7e2d3] rounded-l-md"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const characters =
                        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
                      let result = "";
                      for (let i = 0; i < 8; i++) {
                        result += characters.charAt(
                          Math.floor(Math.random() * characters.length)
                        );
                      }
                      setAgent((prev) => ({
                        ...prev,
                        access_token: result,
                      }));
                    }}
                    className="px-3 py-2 bg-[rgb(255,204,51)] text-[#140d0c] rounded-r-md hover:bg-[rgb(255,204,51)]/90 transition-colors"
                  >
                    Generate
                  </button>
                </div>
                <p className="text-xs text-[#6c6c6c] mt-1">
                  Token used for sharing agent access
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border border-[#e7e2d3]">
                  <div className="flex justify-between items-start">
                    <div>
                      <label className="text-sm font-medium">
                        Outbound Agent
                      </label>
                      <p className="text-xs text-[#6c6c6c] mt-1">
                        Enable if this agent should initiate outbound calls
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agent.is_outbound || false}
                        onChange={(e) =>
                          setAgent((prev) => ({
                            ...prev,
                            is_outbound: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#ffcc33]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffcc33]"></div>
                    </label>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-[#e7e2d3]">
                  <div className="flex justify-between items-start">
                    <div>
                      <label className="text-sm font-medium">Enabled</label>
                      <p className="text-xs text-[#6c6c6c] mt-1">
                        Enable or disable this agent
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agent.enabled || false}
                        onChange={(e) =>
                          setAgent((prev) => ({
                            ...prev,
                            enabled: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#ffcc33]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffcc33]"></div>
                    </label>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-[#e7e2d3]">
                  <div className="flex justify-between items-start">
                    <div>
                      <label className="text-sm font-medium">Public</label>
                      <p className="text-xs text-[#6c6c6c] mt-1">
                        Enable for public access
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agent.is_public || false}
                        onChange={(e) =>
                          setAgent((prev) => ({
                            ...prev,
                            is_public: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#ffcc33]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffcc33]"></div>
                    </label>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-[#e7e2d3]">
                  <div className="flex justify-between items-start">
                    <div>
                      <label className="text-sm font-medium">
                        Can Disconnect
                      </label>
                      <p className="text-xs text-[#6c6c6c] mt-1">
                        Enable if this agent should be able to disconnect from
                        the call
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agent.can_disconnect || false}
                        onChange={(e) =>
                          setAgent((prev) => ({
                            ...prev,
                            can_disconnect: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#ffcc33]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffcc33]"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#f2efe3] p-6 rounded-lg border border-[#e7e2d3]">
            <h3 className="text-lg font-medium mb-4">Messages</h3>
            <div className="space-y-4">
              <div className={`${!agent.is_outbound ? "opacity-50" : ""}`}>
                <div className="flex items-center gap-2 mb-1">
                  <label className="block text-sm font-medium">
                    Startup Message
                  </label>
                  {!agent.is_outbound && (
                    <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
                      Requires Outbound Agent
                    </span>
                  )}
                </div>
                <textarea
                  name="startup_message"
                  value={agent.startup_message || ""}
                  onChange={handleInputChange}
                  disabled={!agent.is_outbound}
                  rows={4}
                  className={`w-full px-3 py-2 border border-[#e7e2d3] rounded-md font-mono text-sm ${!agent.is_outbound ? "bg-gray-50 cursor-not-allowed" : ""
                    }`}
                />
                <p className="text-xs text-[#6c6c6c] mt-1">
                  {agent.is_outbound
                    ? "This message will be spoken when the agent is connected to the call"
                    : "Startup message is only available for outbound agents who initiate calls"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  System Prompt
                </label>
                <textarea
                  name="system_prompt"
                  value={agent.system_prompt || ""}
                  onChange={handleInputChange}
                  onClick={() => setIsPromptModalOpen(true)}
                  rows={6}
                  className="w-full px-3 py-2 border border-[#e7e2d3] rounded-md cursor-pointer hover:border-[rgb(255,204,51)] transition-colors"
                  readOnly
                />
                <p className="text-xs text-[#6c6c6c] mt-1">
                  Click to open advanced editor with markdown preview
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#f2efe3] p-6 rounded-lg border border-[#e7e2d3]">
            <h3 className="text-lg font-medium mb-4">Phone Number Configuration</h3>
            <div className="space-y-4">
              <div className={`${!agent.is_outbound ? "opacity-50" : ""}`}>
                <div className="flex items-center gap-2 mb-3">
                  <label className="block text-sm font-medium">
                    PSTN Provider
                  </label>
                  {!agent.is_outbound && (
                    <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
                      Requires Outbound Agent
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 mb-4">
                  {["plivo", "elission"].map((provider) => (
                    <label key={provider} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="pstn_provider"
                        checked={agent.pstn_provider === provider}
                        onChange={() => handlePstnProviderChange(provider)}
                        disabled={!agent.is_outbound}
                        className="w-4 h-4 text-[rgb(255,204,51)] bg-gray-100 border-gray-300 focus:ring-[rgb(255,204,51)] focus:ring-2"
                      />
                      <span className="text-sm font-medium text-gray-900">
                        {provider === "plivo" ? "Plivo" : "Elission"}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-[#6c6c6c] mb-3">
                  {agent.is_outbound
                    ? "Select the provider for making outbound calls"
                    : "PSTN provider selection is only available for outbound agents"}
                </p>
              </div>

              <div className={`${!agent.is_outbound ? "opacity-50" : ""}`}>
                <div className="flex items-center gap-2 mb-1">
                  <label className="block text-sm font-medium">
                    Agent Phone Number
                  </label>
                  {!agent.is_outbound && (
                    <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
                      Requires Outbound Agent
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  name="phone_number"
                  value={agent.phone_number || ""}
                  onChange={handleInputChange}
                  disabled={!agent.is_outbound}
                  placeholder="+911234567890"
                  className={`w-full px-3 py-2 border border-[#e7e2d3] rounded-md ${!agent.is_outbound ? "bg-gray-50 cursor-not-allowed" : ""
                    }`}
                />
                <p className="text-xs text-[#6c6c6c] mt-1">
                  {agent.is_outbound
                    ? "The phone number that will be displayed when the agent makes outbound calls"
                    : "Phone number is only configurable for outbound agents"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#f2efe3] p-6 rounded-lg border border-[#e7e2d3]">
            <h3 className="text-lg font-medium mb-4">
              Languages Configuration
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Primary Language
                </label>
                <select
                  value={agent.primary_language_id?.toString() || ""}
                  onChange={handlePrimaryLanguageChange}
                  className="w-full px-3 py-2 border border-[#e7e2d3] rounded-md"
                >
                  <option value="">Select a language</option>
                  {languages
                    .filter((language) =>
                      agent.supported_language_ids?.includes(language.id)
                    )
                    .map((language) => (
                      <option key={language.id} value={language.id}>
                        {language.language} ({language.language_code})
                      </option>
                    ))}
                </select>
                {agent.supported_language_ids?.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Please add supported languages first
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Supported Languages
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex flex-wrap gap-2">
                    {agent.supported_language_ids?.map((langId) => (
                      <div
                        key={langId}
                        className="bg-gray-100 px-2 py-1 rounded-full flex items-center"
                      >
                        <span className="text-sm mr-1">
                          {getLanguageName(langId)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSupportedLanguage(langId)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleAddSupportedLanguage}
                    className="px-3 py-1.5 bg-[rgb(255,204,51)] text-[#140d0c] rounded hover:bg-[rgb(255,204,51)]/90 transition-colors flex items-center gap-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          <RealtimeControlsCard
            strategy={agent.strategy || null}
            strategyConfig={agent.strategy_config || null}
            onStrategyConfigChange={handleStrategyConfigChange}
          />

          <StrategySelector
            strategy={agent.strategy || null}
            strategyProviders={safeStrategyProviders}
            strategyConfig={agent.strategy_config || null}
            manifest={providerManifest}
            onStrategyChange={handleStrategyChange}
            onStrategyProvidersChange={handleStrategyProvidersChange}
            onStrategyConfigChange={handleStrategyConfigChange}
          />

          <div className="bg-[#f2efe3] p-6 rounded-lg border border-[#e7e2d3]">
            <h3 className="text-lg font-medium mb-4">Tags</h3>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 mb-4">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() =>
                      isTagSelected(tag.id)
                        ? handleRemoveTagId(tag.id)
                        : handleAddTagId(tag.id)
                    }
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${isTagSelected(tag.id)
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                      }`}
                  >
                    {tag.name}
                    {isTagSelected(tag.id) && <span className="ml-1">✓</span>}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Create new tag"
                  className="flex-1 px-3 py-2 border border-[#e7e2d3] rounded-md"
                />
                <button
                  type="button"
                  onClick={handleCreateNewTag}
                  disabled={!newTagName.trim()}
                  className="px-3 py-2 bg-[rgb(255,204,51)] text-[#140d0c] rounded-md hover:bg-[rgb(255,204,51)]/90 disabled:bg-gray-300 disabled:text-gray-500 transition-colors"
                >
                  Create & Add
                </button>
              </div>
              <p className="text-sm text-[#6c6c6c]">
                Select existing tags or create new ones to categorize the agent
              </p>
            </div>
          </div>

          {validationError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
              {validationError}
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => navigate("/admin/agents")}
              className="px-4 py-2 bg-gray-200 text-[#140d0c] rounded hover:bg-gray-300 transition-colors flex items-center gap-1"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-[rgb(255,204,51)] text-[#140d0c] rounded hover:bg-[rgb(255,204,51)]/90 transition-colors flex items-center gap-1"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Update Agent
            </button>
          </div>
        </div>
      </div>

      {isSubmitting && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <LoadingState
            size="medium"
            padding="large"
            message="Updating agent..."
            fullPage={false}
          />
        </div>
      )}

      <PromptEditorModal
        isOpen={isPromptModalOpen}
        prompt={agent.system_prompt || ""}
        onClose={() => setIsPromptModalOpen(false)}
        onSave={(newPrompt) => {
          setAgent((prev) => ({
            ...prev,
            system_prompt: newPrompt,
          }));
          setIsPromptModalOpen(false);
        }}
      />
    </div>
  );
};

export default AgentEdit;
